import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, Injector } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';
import { RequestOptions } from './api.types';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private readonly injector = inject(Injector);

  private readonly http = inject(HttpClient);

  get<T = any>(
    path: string,
    options: Omit<RequestOptions, 'body'> = {},
  ): Observable<T> {
    return this.request('GET', path, options);
  }

  post<T = any>(
    path: string,
    body: RequestOptions['body'] = {},
    options: Omit<RequestOptions, 'body'> = {},
  ): Observable<T> {
    return this.request('POST', path, { body, ...options });
  }

  put<T = any>(
    path: string,
    body: RequestOptions['body'] = {},
    options: Omit<RequestOptions, 'body'> = {},
  ): Observable<T> {
    return this.request('PUT', path, { body, ...options });
  }

  patch<T = any>(
    path: string,
    body: RequestOptions['body'] = {},
    options: Omit<RequestOptions, 'body'> = {},
  ): Observable<T> {
    return this.request('PATCH', path, { body, ...options });
  }

  delete<T = any>(
    path: string,
    options: Omit<RequestOptions, 'body'> = {},
  ): Observable<T> {
    return this.request('DELETE', path, options);
  }

  private request<T = any>(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    path: string,
    options: RequestOptions,
  ): Observable<T> {
    return this.http
      .request(method, `${environment.apiUrl}${path}`, {
        withCredentials: true,
        ...options,
      })
      .pipe<T>(catchError((error) => this.handleLogout(error)));
  }

  private handleLogout(error: HttpErrorResponse): Observable<never> {
    const { status, message } = error;
    const errorMessage = error.error as string | undefined;

    // Handle logout if status is 401 (Unauthorized) or 0 (Network Error)
    if (status === 401 || status === 0) {
      this.authServiceSignOut();
    }

    return throwError(
      () => errorMessage ?? message ?? 'Unknown error occurred',
    );
  }

  private async authServiceSignOut(): Promise<void> {
    // this is a workaround to avoid circular dependency
    const { AuthService } = await import('../auth/auth.service');
    const authService = this.injector.get(AuthService);
    await authService.signOut('expired');
  }
}
